import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { WebPage } from '../models';

interface WebPageRequest {
  url: string;
  name: string;
  description?: string;
  status?: string;
}

interface WebPageResponse {
  id: number;
  url: string;
  name: string;
  description?: string;
  status: string;
  lastAudit?: string;
  nextAudit?: string;
  securityScore?: number;
  criticalVulnerabilities: number;
  highVulnerabilities: number;
  mediumVulnerabilities: number;
  lowVulnerabilities: number;
  ownerId: number;
  ownerName: string;
  createdAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class WebPageService {
  private apiUrl = '/api/webpages';

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHeaders(): HttpHeaders {
    return this.authService.getAuthHeaders();
  }

  getMyPages(): Observable<WebPage[]> {
    return this.http.get<WebPageResponse[]>(`${this.apiUrl}/my-pages`, {
      headers: this.getHeaders()
    }).pipe(
      map(responses => responses.map(r => this.mapToWebPage(r)))
    );
  }

  getAllPages(): Observable<WebPage[]> {
    return this.http.get<WebPageResponse[]>(this.apiUrl, {
      headers: this.getHeaders()
    }).pipe(
      map(responses => responses.map(r => this.mapToWebPage(r)))
    );
  }

  getPageById(id: number): Observable<WebPage> {
    return this.http.get<WebPageResponse>(`${this.apiUrl}/${id}`, {
      headers: this.getHeaders()
    }).pipe(
      map(response => this.mapToWebPage(response))
    );
  }

  createPage(request: WebPageRequest): Observable<WebPage> {
    return this.http.post<WebPage>(this.apiUrl, request, {
      headers: this.getHeaders()
    });
  }

  updatePage(id: number, request: WebPageRequest): Observable<WebPage> {
    return this.http.put<WebPage>(`${this.apiUrl}/${id}`, request, {
      headers: this.getHeaders()
    });
  }

  deletePage(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`, {
      headers: this.getHeaders()
    });
  }

  private mapToWebPage(response: WebPageResponse): WebPage {
    return {
      id: response.id,
      url: response.url,
      name: response.name,
      description: response.description,
      status: response.status as 'active' | 'inactive' | 'pending',
      lastAudit: response.lastAudit ? new Date(response.lastAudit) : undefined,
      nextAudit: response.nextAudit ? new Date(response.nextAudit) : undefined,
      securityScore: response.securityScore,
      vulnerabilities: {
        critical: response.criticalVulnerabilities,
        high: response.highVulnerabilities,
        medium: response.mediumVulnerabilities,
        low: response.lowVulnerabilities
      },
      owner: response.ownerName,
      createdAt: new Date(response.createdAt)
    };
  }
}
